import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * A sunken track holding one raised thumb.
 *
 * The same object as the order-type switch (`TradeFormComponents/TabsComponent`),
 * the header's nav rail and the table's tabs — generalised, because the
 * liquidity page needs three of them (deposit type, chart range, the deposits
 * tabs) and the ticket's version is welded to a two-item Market/Limit list.
 *
 * The thumb is *measured* off the selected button rather than derived from the
 * item count: the labels are different widths ("Active Deposits" against
 * "Withdrawal Requests"), so equal thirds would leave the thumb short of one
 * label and over-hanging another.
 *
 * @param {Array<{value:*, label:React.ReactNode, badge?:React.ReactNode, disabled?:boolean}>} items
 * @param {*} value           currently selected item's `value`
 * @param {Function} onChange called with the new value
 * @param {boolean} grow      items share the width equally instead of sizing to their label
 */
const SegmentedTabs = ({
  items,
  value,
  onChange,
  grow = false,
  size = 30,
  label = "",
  className = "",
  style,
}) => {
  const trackRef = useRef(null);
  const btnRefs = useRef([]);
  const [seat, setSeat] = useState(null);
  const settled = useRef(false);

  const index = items.findIndex((it) => it.value === value);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const btn = btnRefs.current[index];
    if (!track || !btn) return;
    const next = { x: btn.offsetLeft, w: btn.offsetWidth };
    setSeat((prev) => (prev && prev.x === next.x && prev.w === next.w ? prev : next));
  }, [index]);

  useLayoutEffect(() => {
    measure();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [measure]);

  // The first seat is placed, not animated — otherwise the thumb slides in
  // from the left edge on every mount.
  const idle = !settled.current;
  if (seat && !settled.current) settled.current = true;

  return (
    <div
      ref={trackRef}
      role="tablist"
      aria-label={label}
      // `ce-raw` exempts the track from the dark-mode sweep, which would
      // otherwise repaint the thumb's `--raise` gradient as a flat surface.
      className={`ce-raw ce-seg ${className}`.trim()}
      style={{ gap: 2, ...style }}
    >
      {seat && (
        <span
          aria-hidden="true"
          className="ce-seg-thumb"
          style={{
            left: 0,
            width: seat.w,
            transform: `translateX(${seat.x}px)`,
            transition: idle ? "none" : undefined,
          }}
        />
      )}
      {items.map((item, i) => {
        const active = item.value === value;
        return (
          <button
            key={String(item.value)}
            ref={(el) => { btnRefs.current[i] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.value)}
            className="ce-seg-item"
            style={{
              flex: grow ? "1 1 0" : "0 0 auto",
              height: size,
              padding: "0 16px",
              // A <button> with no author background falls back to the UA's
              // buttonface — an opaque grey under `color-scheme: dark` that
              // would paint over the travelling thumb.
              WebkitAppearance: "none",
              appearance: "none",
              backgroundColor: "transparent",
              backgroundImage: "none",
              color: active ? "var(--cat-accent, #5B1DEE)" : "var(--ink-3)",
              fontWeight: active ? 600 : 500,
              fontSize: 13.5,
              opacity: item.disabled ? 0.5 : 1,
              cursor: item.disabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
            {item.badge}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedTabs;
